interface SerializableConstructor<T> {
  new (data: Partial<T>): T;
  fromJSON(data: Record<string, any>): T;
}

export default abstract class Serializable<T extends Serializable<T>> {
  /**
   * Convert the instance to a plain object (JSON representation)
   */
  public toJSON(): Record<string, any> {
    const transformValue = (value: any): any => {
      if (value instanceof Date) {
        return value.toISOString(); // Serialize Date to ISO string
      }
      if (value instanceof Serializable) {
        return value.toJSON(); // Recursively flatten Serializable objects
      }
      if (Array.isArray(value)) {
        return value.map(transformValue); // Recursively process arrays
      }
      if (value && typeof value === "object") {
        // Process plain objects recursively
        return Object.keys(value).reduce((acc, key) => {
          acc[key] = transformValue(value[key]);
          return acc;
        }, {} as Record<string, any>);
      }
      return value; // Retain primitives and other types as is
    };

    // Transform all properties of the current instance
    return Object.keys(this).reduce((plainObject, key) => {
      const value = (this as any)[key];
      plainObject[key] = transformValue(value);
      return plainObject;
    }, {} as Record<string, any>);
  }

  /**
   * Factory method to reconstruct the instance from a JSON object
   */
  public static fromJSON<T extends Serializable<T>>(
    this: SerializableConstructor<T>,
    data: Record<string, any>
  ): T {
    const transformValue = (value: any): any => {
      if (Array.isArray(value)) {
        // Handle arrays of mixed types
        return value.map(transformValue);
      }

      if (typeof value === "string" && !isNaN(Date.parse(value))) {
        // Convert ISO string to Date object
        return new Date(value);
      }

      if (value && typeof value === "object") {
        // Check for Serializable objects
        if (value.toJSON || this.prototype.toJSON) {
          return (this as any).fromJSON(value); // Recursively deserialize Serializable objects
        }
        // Handle plain objects
        return Object.keys(value).reduce((acc, key) => {
          acc[key] = transformValue(value[key]);
          return acc;
        }, {} as Record<string, any>);
      }

      return value; // Retain primitives and other types as is
    };

    const instance = new this(data as Partial<T>);

    Object.keys(data).forEach((key) => {
      (instance as any)[key] = transformValue(data[key]);
    });

    return instance;
  }

  /**
   * Convert a string to a Date object or null if invalid
   * @param dateStr - The date string to convert
   */
  public static toDate(dateStr: Date | string | null | undefined): Date | null {
    if (!dateStr) {
      return null;
    }

    // If the input is already a Date, return it directly
    if (dateStr instanceof Date) {
      return isNaN(dateStr.getTime()) ? null : dateStr;
    }

    // Otherwise, try to parse it as a string
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  }
}
